import React, { useState } from "react";
import { Icon, IconButton, Portal, Spinner } from "@chakra-ui/react";
import { Menu, MenuButton, MenuList, MenuItem } from "@chakra-ui/react";
import { AppIcons } from "constants/icons";

export type DownloadMenuItem = {
  label: string;
  icon: keyof typeof AppIcons;
  onClick: () => void | Promise<void>;
};

// Consistent download icon button with a dropdown.
//
// When an item's onClick returns a Promise, the MenuButton icon swaps to a
// Spinner until the download finishes or fails. The menu closes immediately
// on selection — the spinner remains visible on the button during the async work.
export const DownloadMenu: React.FC<{ ariaLabel: string; items: DownloadMenuItem[] }> = ({ ariaLabel, items }) => {
  const [loadingLabel, setLoadingLabel] = useState<string | null>(null);

  const handleClick = async (item: DownloadMenuItem) => {
    if (loadingLabel) return;
    const result = item.onClick();
    if (result instanceof Promise) {
      setLoadingLabel(item.label);
      try {
        await result;
      } finally {
        setLoadingLabel(null);
      }
    }
  };

  return (
    <Menu placement="bottom-end" gutter={2} closeOnSelect={true}>
      <MenuButton
        as={IconButton}
        size="xs"
        color="gray.600"
        bg="transparent"
        _hover={{ bg: "gray.50", color: "gray.800" }}
        aria-label={ariaLabel}
        isDisabled={!!loadingLabel}
        icon={loadingLabel ? <Spinner size="xs" color="purple.500" /> : <Icon as={AppIcons.download} boxSize={6} />}
      />
      <Portal>
        <MenuList minW="160px" zIndex={1000} shadow="lg" border="1px solid" borderColor="gray.200" p={1}>
          {items.map(item => (
            <MenuItem
              key={item.label}
              icon={<Icon as={AppIcons[item.icon]} boxSize={4} />}
              onClick={() => handleClick(item)}
              fontSize="sm"
            >
              {item.label}
            </MenuItem>
          ))}
        </MenuList>
      </Portal>
    </Menu>
  );
};
